import { Widget } from "@foretagsplatsen/widgetjs";

/**
 * `TableWidget` displays a list of items as a table.
 *
 * This is rendering a simple 'static' table.
 * The actual rendering is handled by a passed in `callback`,
 * rowItemCellsRenderer(rowItem), which makes the rendering based on rows.
 *
 * If you need to define a table by columns, and/or need sorting GridWidget
 * is probably a better choice.
 *
 * @param {string} [spec.bodyCssClass] - Decide the css class for the table body.
 * @param {string} [spec.footerCssClass] - Decide the css class for the table footer.
 * @param {string} [spec.headerCssClass] - Decide the css class for the table header.
 * @param {string} [spec.tableCssClass] - Decide the css class for the table.
 * @param {object[]} [spec.rowItems] - A rowItem is any object that
 *	rowItemCellsRenderer(rowItem) will render as a collection of <td> nodes.
 * @param {Renderer} [spec.rowItemCellsRenderer] - Takes a rowItem as argument and
 *  takes care of rendering the `<td>` nodes.
 * @param {Renderable} [spec.footerCells] - `<td>` nodes for the table footer.
 * @param {headerDefinition[]} [spec.tableHeaders] - Headers for the tablecells.
 *  Can either be a plain string.
 *  Can also be an object with a string and a cssClass
 *  example :
			{
			name: _("Debit"),
			class: "text-right",
		},
 */
export default class TableWidget extends Widget {
	constructor({
		bodyCssClass,
		rowItems,
		rowItemCellsRenderer,
		footerCells,
		footerCssClass,
		headerCssClass,
		tableCssClass,
		tableHeaders,
	} = {}) {
		super(...arguments);
		this._bodyCssClass = bodyCssClass || "table-condensed";
		this._rowItems = rowItems;
		this._rowItemCellsRenderer = rowItemCellsRenderer;
		this._footerCells = footerCells;
		this._footerCssClass = footerCssClass || "table-condensed";
		this._headerCssClass = headerCssClass;

		this._tableCssClass =
			tableCssClass ||
			"table table-auto table-striped table-condensed table-small table-hover";

		this._tableHeaders = tableHeaders;
	}

	renderContentOn(html) {
		html.table(
			{
				klass: this._tableCssClass,
			},
			this._tableHeaders ? this._renderHeaderOn.bind(this) : "",
			this._rowItemCellsRenderer && this._rowItems
				? this._renderBodyOn.bind(this)
				: "",
			this._footerCells ? this._renderFooterOn.bind(this) : "",
		);
	}

	_renderBodyOn(html) {
		html.tbody(
			{ class: this._bodyCssClass },
			this._rowItems.map(
				(item) => (html) => html.tr(this._rowItemCellsRenderer(item)),
			),
		);
	}

	_renderFooterOn(html) {
		html.tfoot({ class: this._footerCssClass }, html.tr(this._footerCells));
	}

	_renderHeaderOn(html) {
		html.thead(
			{ class: this._headerCssClass },
			html.tr(
				this._tableHeaders.map((header) =>
					header.name
						? html.th(header, header.name)
						: html.th(header),
				),
			),
		);
	}
}
